import { keyframes } from '@emotion/react'
import type { Tokens } from '../../../theming/types'
import type { ProgressCircleProps } from '..'

const CIRCUMFERENCE_SMALL = '37.69911184307752'
export const CIRCUMFERENCE_MEDIUM = '106.81415022205297'
const CIRCUMFERENCE_LARGE = '226.1946710584651'

const expandCollapseSmall = keyframes`
  0% {
    stroke-dasharray: 3px, 38px;
    stroke-dashoffset: 0px;
  }
  50% {
    stroke-dasharray: 29px, 38px;
    stroke-dashoffset: -5px;
  }
  100% {
    stroke-dasharray: 29px, 38px;
    stroke-dashoffset: -35px;
  }
`

const expandCollapseMedium = keyframes`
  0% {
    stroke-dasharray: 9px, 106px;
    stroke-dashoffset: 0px;
  }
  50% {
    stroke-dasharray: 80px, 106px;
    stroke-dashoffset: -13px;
  }
  100% {
    stroke-dasharray: 80px, 106px;
    stroke-dashoffset: -97px;
  }
`

const expandCollapseLarge = keyframes`
  0% {
    stroke-dasharray: 19px, 226px;
    stroke-dashoffset: 0px;
  }
  50% {
    stroke-dasharray: 170px, 226px;
    stroke-dashoffset: -28px;
  }
  100% {
    stroke-dasharray: 170px, 226px;
    stroke-dashoffset: -207px;
  }
`

export default (config: {
  props: Pick<ProgressCircleProps, 'dark' | 'size'>
  tokens: Tokens
}) => {
  const { props, tokens } = config

  const sizes = {
    small: {
      height: '16px',
      centerPointX: 8,
      centerPointY: 8,
      radius: 7,
      fillStrokeWidth: 2,
      trackStrokeWidth: 2,
      width: '16px',
      strokeDasharray: CIRCUMFERENCE_SMALL,
      indeterminate: {
        circleFill: {
          animation: `${expandCollapseSmall} 1.4s ease-in-out infinite`,
          strokeDasharray: '29px, 38px',
          strokeDashoffset: '0px',
        },
      },
      text: {
        marginInlineStart: tokens.spacingGap100,
      },
    },

    medium: {
      height: '40px',
      centerPointX: 20,
      centerPointY: 20,
      radius: 18,
      fillStrokeWidth: 4,
      trackStrokeWidth: 4,
      width: '40px',
      strokeDasharray: CIRCUMFERENCE_MEDIUM,
      indeterminate: {
        circleFill: {
          animation: `${expandCollapseMedium} 1.4s ease-in-out infinite`,
          strokeDasharray: '80px, 106px',
          strokeDashoffset: '0px',
        },
      },
      text: {
        marginInlineStart: tokens.spacingGap150,
      },
    },

    large: {
      height: '80px',
      centerPointX: 40,
      centerPointY: 40,
      radius: 36,
      fillStrokeWidth: 8,
      trackStrokeWidth: 8,
      width: '80px',
      strokeDasharray: CIRCUMFERENCE_LARGE,
      // Will be removed in FRNTEND-3500
      indeterminate: {
        circleFill: {
          animation: `${expandCollapseLarge} 1.4s ease-in-out infinite`,
          strokeDasharray: '170px, 226px',
          strokeDashoffset: '0px',
        },
      },
      text: {
        marginInlineStart: tokens.spacingGap200,
      },
    },
  }

  return {
    default: {
      circleFill: {
        stroke: props.dark
          ? tokens.dark.barFillColorDefault
          : tokens.barFillColorDefault,
        strokeLinecap: 'round',
      },

      circleTrack: {
        stroke: props.dark
          ? tokens.dark.barTrackColorDefault
          : tokens.barTrackColorDefault,
      },

      text: {
        color: props.dark ? tokens.fontColorInverse : tokens.fontColorDefault,
      },
    },

    subtle: {
      circleFill: {
        stroke: props.dark
          ? tokens.dark.barFillColorSubtle
          : tokens.barFillColorSubtle,
      },
    },

    size: {
      ...sizes[props.size!],
    },
  } as const
}
